/**
 * API client with JWT authentication.
 *
 * Token model (issues #609 + #972):
 * - The 15-min ACCESS token lives in memory + sessionStorage (per-tab).
 * - The 7-day REFRESH token is NEVER in JS-readable storage. It is carried in an
 *   HttpOnly cookie set by the backend (path-scoped to the refresh endpoint), so
 *   it survives a new tab / cold reload / bookmark while staying invisible to JS
 *   (`document.cookie` does not expose it) -- preserving #609's XSS goal.
 * - On 401 (or on boot with no usable access token), we silently mint a fresh
 *   access token by POSTing to the refresh endpoint with `credentials:"include"`
 *   so the browser attaches the HttpOnly refresh cookie.
 */

import { isLogoutPending, setLogoutPending } from "../lib/logoutPending";

const API_BASE = "/api/v1";

/**
 * Double-submit CSRF token for the cookie-based refresh (issue #972).
 *
 * The backend sets a readable (non-HttpOnly) `csrf_refresh` cookie alongside the
 * HttpOnly refresh cookie. We echo its value in the `X-CSRF-Refresh` header on
 * every refresh/logout call; the backend rejects a cookie refresh whose header
 * does not match the cookie. A cross-origin attacker can make the browser SEND
 * the refresh cookie but cannot READ this companion cookie to forge the header.
 */
const CSRF_REFRESH_COOKIE = "csrf_refresh";
const CSRF_REFRESH_HEADER = "X-CSRF-Refresh";

function readCsrfRefreshCookie(): string {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + CSRF_REFRESH_COOKIE + "=([^;]*)"),
  );
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

/**
 * DOM CustomEvent name dispatched when the user's session expires mid-flight
 * (401 that cannot be refreshed). AuthProvider listens for this to clear React
 * auth state, which makes ProtectedRoute redirect to /login.
 */
const SESSION_EXPIRED_EVENT = "lwp:session-expired";

/** SessionStorage key set before the redirect so Login.tsx can show a message. */
export const SESSION_EXPIRED_STORAGE_KEY = "lwp_session_expired";

/**
 * Subscribe to session-expired events. Returns an unsubscribe function
 * (suitable as a useEffect cleanup return).
 */
export function onSessionExpired(cb: () => void): () => void {
  window.addEventListener(SESSION_EXPIRED_EVENT, cb);
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, cb);
}

interface Tokens {
  access: string;
  refresh: string;
}

/** Deal-finance permission flags (D4-AUTH-001). */
export interface PermissionFlags {
  capture_deal: boolean;
  view_commission: boolean;
  enter_own_commission: boolean;
  authorize_commission: boolean;
  process_payment: boolean;
  /** D5-RBP-001: Manager — may sign off deals/mandates. */
  sign_off_deals: boolean;
  /** D5-RBP-001: Accounts — may invest a deal's deposit. */
  invest_funds: boolean;
  /** WP8a (#1299): scoped user management (Manager / Office Admin). */
  manage_users: boolean;
  /** #1411 (EPIC #1410): Manager — read-only company-wide oversight of the
   *  operational dashboards + Leaderboard/Sales Targets. Explicitly NOT
   *  view_commission (company commission stays masked). */
  view_management_dashboards: boolean;
  /** D5-RBP-003 (#1412, EPIC #1410): Office Admin — may list/create a property
   *  on behalf of the agency WITHOUT the broad capture_deal back-office bundle.
   *  A plain agent lists via its own book (unaffected); admins hold it as a
   *  superset. */
  list_properties: boolean;
  /** EPIC #2033 WP11 (#2090): HR — agent-compliance management capability (owns
   *  the roster, upload/verify, reason codes, counter-sign, document-expiry
   *  reminders). Held by the HR preset; admins hold it as a superset. */
  manage_compliance: boolean;
  /** #2183: may capture a NEW deal ON BEHALF of another agent — selecting the
   *  lead agent (and so the auto commission split) instead of being forced to
   *  self. Lets management load a deal for an absent/sick agent. Held by MANAGER
   *  + PARALEGAL; admins hold it as a superset. */
  capture_on_behalf: boolean;
  /** Role-matrix v2 (#2317, A3 — #2261/2265): Finance — may see outstanding FICA
   *  status on ALL deals (not just payout-stage). Held by FINANCE /
   *  FINANCE_ACCOUNTS; admins hold it as a superset. */
  view_all_fica: boolean;
  /** Role-matrix v2 (#2317, A4 — #2277/2279): may assign agents to areas. Held by
   *  MANAGER + HR; admins hold it as a superset (previously admin-only). */
  assign_areas: boolean;
  /** Role-matrix v2 (#2317, A2/C9 — #2271/#2250): maker-checker primitive — a
   *  MANAGER may INITIATE a sensitive action (create an admin, edit a sales
   *  target) pending Super-User authorization. Flag-only in PR-A; the approval
   *  workflow/UI is PR-B. Held by MANAGER; admins hold it as a superset. */
  request_privileged_authorization: boolean;
  /** Role-matrix v2 (#2317, A5 — #2287): CANVASSER add-only contact capability —
   *  may ADD a phone number to an allocated contact but NEVER edit existing
   *  contact details. Held by the CANVASSER preset; admins hold it as a superset. */
  add_contact_numbers: boolean;
  /** #2813: the restricted HR/personnel document-template capability -- may
   *  upload/replace the agency's BLANK personnel contract templates. A genuine
   *  per-user grant (Natalie/Cam, optionally the paralegal), never bundled into
   *  a preset; admins hold it as a superset. */
  manage_hr_documents: boolean;
  /** D9-INT-006 (#3539): may read a deal buyer's bond pack (structured buyer
   *  finance) and review/apply a submitted buyer intake. On for a plain agent,
   *  PARALEGAL, FINANCE and FINANCE_ACCOUNTS; off for the value-free presets;
   *  admins hold it as a superset. Not view_commission. */
  view_bond_pack: boolean;
}

export type PermissionPreset =
  | "none"
  | "paralegal"
  | "finance"
  // #1506: combined Finance/Accounts preset (one person at LWP).
  | "finance_accounts"
  | "manager"
  | "accounts"
  | "office_admin"
  // EPIC #2033 WP11 (#2090): HR — agent-compliance manager.
  | "hr"
  // D5-RBP-005 (#4175): combined HR + Paralegal desk (one login, both desks).
  | "hr_paralegal"
  // Role-matrix v2 (#2317, A5 — #2287): the restricted CANVASSER role.
  | "canvasser"
  | "custom";

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: "agent" | "admin";
  phone: string;
  is_active: boolean;
  must_reset_password: boolean;
  /**
   * D8-ANA-W1-009: True until the user accepts the CURRENT Terms & Conditions
   * version. Derived server-side (re-computed on every ``/auth/me/`` fetch),
   * so a version bump re-prompts every user automatically -- see
   * ``ProtectedRoute.tsx``'s blocking redirect to ``/accept-terms``.
   */
  must_accept_terms?: boolean;
  /** Most recent T&Cs version this user has ever accepted (may be stale), or null. */
  terms_accepted_version?: string | null;
  /** When ``terms_accepted_version`` was accepted, or null if never accepted. */
  terms_accepted_at?: string | null;
  /** D4-AUTH-001: assigned permission preset (label only). */
  permission_preset?: PermissionPreset;
  /** D4-AUTH-001: effective permission flags (admin shows all-true). */
  permission_flags?: PermissionFlags;
  /**
   * D2-FRM-018 (#3714): office canvasser -- may read and canvass every
   * non-retired farm area without owning one. Surfaces the Farm nav for a
   * back-office preset that would otherwise hide it; the server is the gate.
   */
  farm_wide_canvass?: boolean;
  /**
   * D2-USR-010 (#4001): the user still works their own book (at least one own
   * farm area). Keeps the own-book nav for a back-office preset holder who is
   * also a working agent. Nav-only; the server scopes every endpoint.
   */
  has_own_book?: boolean;
  /**
   * D2-SEC-014 (#4081): the agency's idle sign-out, in minutes (0 = off).
   * Agency config carried on the login / me payload for IdleSignOut.
   */
  idle_timeout_minutes?: number;
  /**
   * Django superuser (account owner). Read-only. Gates the super-admin-only
   * Integrations settings page client-side (#1932); the server is the real gate.
   */
  is_superuser?: boolean;
  /**
   * D4-FBK-054 (#4715): the server serves this user the consolidated "all"
   * feedback-reports list (its rule: staff/super-user or ``role == 'admin'``).
   * Picks the reports page's opening Status filter; the server is the gate.
   */
  sees_all_feedback?: boolean;
  /**
   * D4-ONB-006: show the first-login guided-tours welcome prompt exactly once.
   * Set when an invite link is issued; cleared the moment the user answers the
   * prompt (taken OR dismissed) via ``answerWelcomePrompt``. Existing accounts
   * (never re-invited) stay false and never see the prompt.
   */
  welcome_prompt_eligible?: boolean;
  /**
   * #2254: manager data-visibility floor as ISO ``YYYY-MM-DD``, or null.
   * Non-null ONLY for a non-admin management-oversight user (the MANAGER
   * preset); the UI uses it to set the ``min`` on oversight date pickers and to
   * surface the limit. Admin/finance get null (full history). The server clamp
   * is the real gate -- this is a UX hint only.
   */
  manager_data_floor?: string | null;
  /**
   * #2445: minimum financial-year-end (FYE) label a floored manager may see
   * (e.g. 2026), or null for admin/finance/plain agent (full history). The UI
   * uses it to floor the FY-year dropdowns on the market-share / bond-analytics
   * oversight pages. The server clamp is the real gate -- this is a UX hint.
   */
  manager_floor_fye?: number | null;
  /**
   * D2-SEC-011 (#3944): present only while a super-user/manager is viewing the
   * CRM as this agent (read only). The profile above is then the AGENT's; this
   * names the real viewer and when the session ends. Drives the banner.
   */
  view_as?: {
    viewer: { id: number; name: string };
    target: { id: number; name: string };
    expires_at: string;
    read_only: true;
  };
  date_joined: string;
  last_login: string | null;
}

interface LoginResponse {
  access: string;
  /** Deprecated (#972): refresh token now arrives via the HttpOnly cookie. */
  refresh?: string;
  user: User;
}

// Token storage (issues #609 + #972).
//
// SECURITY: the 7-day **refresh** token is the high-value credential, so it is
// NEVER held in JS-readable storage. It lives ONLY in an HttpOnly cookie set by
// the backend (#972) -- unreadable by JS, but shared across tabs and durable
// across a cold reload, so the session is restorable everywhere. Only the
// short-lived (15 min) **access** token is kept in memory + sessionStorage so a
// same-tab refresh stays signed in until the access token naturally expires;
// when it is missing/expired we silently mint a new one from the cookie.
//
// The in-memory `tokens.refresh` field is now always "" -- kept for the Tokens
// shape and legacy callers; the refresh token itself is never assigned here.
let tokens: Tokens | null = null;

// Only the access token is persisted; the refresh token stays in memory.
const ACCESS_STORAGE_KEY = "lwp_auth_access";
// Legacy key (pre-#609) that stored BOTH tokens -- purged on load so a stale
// refresh token cannot linger in web storage after upgrade.
const LEGACY_STORAGE_KEY = "lwp_auth_tokens";

function persistTokens(t: Tokens | null) {
  tokens = t;
  if (t) {
    // Persist ONLY the access token; the refresh token is held in `tokens`.
    sessionStorage.setItem(ACCESS_STORAGE_KEY, t.access);
  } else {
    sessionStorage.removeItem(ACCESS_STORAGE_KEY);
  }
  // Always clear the legacy combined-token entry.
  sessionStorage.removeItem(LEGACY_STORAGE_KEY);
}

function loadTokens(): Tokens | null {
  if (tokens) return tokens;
  // Purge any legacy combined-token entry that held a refresh token in storage.
  if (sessionStorage.getItem(LEGACY_STORAGE_KEY)) {
    sessionStorage.removeItem(LEGACY_STORAGE_KEY);
  }
  const access = sessionStorage.getItem(ACCESS_STORAGE_KEY);
  if (access) {
    // Refresh token is in-memory only; after a hard refresh it is empty and the
    // session lives off the access token until it expires.
    tokens = { access, refresh: "" };
    return tokens;
  }
  return null;
}

export function getAccessToken(): string | null {
  return loadTokens()?.access ?? null;
}

export function clearTokens(): void {
  persistTokens(null);
}

export function isAuthenticated(): boolean {
  return !!loadTokens()?.access;
}

/**
 * Run the session-expired bookkeeping: clear tokens, set the storage flag so
 * Login.tsx can show a message, and dispatch the session-expired DOM event so
 * AuthProvider clears React auth state (→ ProtectedRoute redirects to /login).
 *
 * Exported so non-`apiRequest` callers (e.g. the multipart feedback POST in
 * feedback.ts) can trigger the IDENTICAL re-login flow instead of redefining
 * the (module-private) event string or surfacing a raw backend auth error.
 */
export function notifySessionExpired(): void {
  clearTokens();
  sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, "1");
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

/**
 * Pure predicate: does a response look like a RECOVERABLE auth-expiry (issue #2376)?
 *
 * A stale/missing JWT can surface as EITHER an HTTP 401 OR an HTTP 403. DRF
 * raises `NotAuthenticated` (default detail *"Authentication credentials were not
 * provided."*) with a **401 only when the first authenticator supplies a
 * `WWW-Authenticate` header, otherwise 403** — so an `IsAuthenticated` endpoint
 * can legitimately answer a missing/expired credential with a 403. The old code
 * only refreshed+retried on 401, so a 403-NotAuthenticated fell straight through
 * and the raw DRF string was rendered to the user (the feedback-modal symptom).
 *
 * This treats a 401 as always auth-expiry, and a 403 as auth-expiry ONLY when the
 * body is the DRF `NotAuthenticated` shape (`code === "not_authenticated"` or the
 * canonical detail string). A genuine `PermissionDenied` 403 ("You do not have
 * permission …") is NOT auth-expiry, so its specific message is preserved.
 */
export function isNotAuthenticatedResponse(
  status: number,
  data: unknown,
): boolean {
  if (status === 401) return true;
  if (status !== 403) return false;
  const body = (data ?? {}) as Record<string, unknown>;
  const code = typeof body.code === "string" ? body.code.toLowerCase() : "";
  if (code === "not_authenticated") return true;
  const detail = typeof body.detail === "string" ? body.detail.toLowerCase() : "";
  return detail.includes("authentication credentials were not provided");
}

/**
 * Async peek: is `resp` a recoverable auth-expiry (401, or a 403 DRF
 * NotAuthenticated) that should drive the refresh → retry → friendly flow?
 * Issue #2376.
 *
 * A 401 needs no body read. A 403 is inspected via `resp.clone()` when available
 * so the caller can still read the ORIGINAL body (a real `Response` body can only
 * be consumed once); test mocks without `clone()` fall back to reading directly
 * (their `json()` is a plain repeatable function). Any read/parse failure is
 * treated as "not auth-expiry" so a non-JSON 403 keeps its normal handling.
 */
export async function responseIsAuthExpiry(resp: Response): Promise<boolean> {
  if (resp.status === 401) return true;
  if (resp.status !== 403) return false;
  try {
    const source = typeof resp.clone === "function" ? resp.clone() : resp;
    const data = await source.json();
    return isNotAuthenticatedResponse(403, data);
  } catch {
    return false;
  }
}

/**
 * Transparent 429 (Too Many Requests) backoff/retry (issue #1803).
 *
 * The interactive CRM SPA fan-fires many requests per page load (a list page
 * plus several filter option-sets, branding/identity/me, plus the notification
 * bell polling) so a brief burst can momentarily trip the global DRF `user`
 * throttle. Rather than surface a hard 429 to the user, `apiRequest` retries the
 * request a small, bounded number of times with a short backoff:
 *
 * - We honour the server's `Retry-After` header (DRF emits it as an integer
 *   number of seconds), but CAP the wait at `MAX_RETRY_AFTER_SECONDS` so a long
 *   "39 seconds" value never hangs the UI -- we wait a bounded amount then retry.
 * - If `Retry-After` is absent/unparseable we fall back to an exponential
 *   backoff (~1s, 2s) keyed off the attempt number.
 * - After `MAX_THROTTLE_RETRIES` retries we give up and throw the normal
 *   `ApiError(429, ...)` so callers can show a calm inline message.
 * - The backoff sleep honours the caller's AbortSignal: if the signal aborts
 *   mid-wait we reject immediately and do NOT retry.
 */
const MAX_THROTTLE_RETRIES = 2;
const MAX_RETRY_AFTER_SECONDS = 8;

/**
 * Promise-based delay that resolves after `ms` milliseconds, or rejects early if
 * the optional AbortSignal fires (so a 429 backoff does not retry after the
 * caller has aborted). Issue #1803.
 */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Compute the backoff (in ms) before retrying a 429, capped so a large
 * `Retry-After` cannot stall the UI. `attempt` is 0-based (first retry = 0).
 * Issue #1803.
 */
function throttleBackoffMs(retryAfterHeader: string | null, attempt: number): number {
  const parsed = retryAfterHeader ? parseInt(retryAfterHeader, 10) : NaN;
  const seconds = Number.isFinite(parsed) && parsed > 0
    ? Math.min(parsed, MAX_RETRY_AFTER_SECONDS)
    : Math.min(2 ** attempt, MAX_RETRY_AFTER_SECONDS); // ~1s, 2s, ...
  return seconds * 1000;
}

/**
 * Make an authenticated API request. Automatically attaches JWT bearer token
 * and retries once on 401 by refreshing the access token. On 429 (throttled) it
 * transparently backs off and retries a small, bounded number of times honouring
 * `Retry-After` (issue #1803) before surfacing the error.
 *
 * An optional AbortSignal can be passed to cancel in-flight requests (e.g.
 * when a React useEffect cleanup fires before the fetch completes).
 */
async function apiRequest<T>(
  url: string,
  options: RequestInit = {},
  signal?: AbortSignal
): Promise<T> {
  // One full attempt: applies the bearer, fires the fetch, and handles the 401
  // silent-refresh-and-retry-once path. Returns the final Response so the outer
  // loop can decide whether to retry on a 429. (#1803 wraps this in a backoff
  // loop; the 401 logic is unchanged.)
  const attemptOnce = async (): Promise<Response> => {
    const currentTokens = loadTokens();

    // A FormData body must NOT carry an explicit Content-Type -- the browser
    // sets ``multipart/form-data`` with the correct boundary itself (#2102).
    const isFormData =
      typeof FormData !== "undefined" && options.body instanceof FormData;

    const headers: Record<string, string> = {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(options.headers as Record<string, string> || {}),
    };

    if (currentTokens?.access) {
      headers["Authorization"] = `Bearer ${currentTokens.access}`;
    }

    const fetchOptions: RequestInit = {
      ...options,
      headers,
      ...(signal ? { signal } : {}),
    };

    const response = await fetch(`${API_BASE}${url}`, fetchOptions);

    // On 401 -- OR a 403 whose body is the DRF NotAuthenticated shape (#2376) --
    // try ONCE to silently mint a new access token from the HttpOnly refresh
    // cookie (#972), then retry. A missing/expired credential can surface as a
    // 403 (DRF returns 401 only when an authenticator adds WWW-Authenticate), so
    // both must run the same silent-refresh recovery rather than leak the raw
    // "Authentication credentials were not provided." string. A genuine
    // PermissionDenied 403 is NOT matched and keeps its own message.
    if (await responseIsAuthExpiry(response)) {
      const refreshed = await refreshSession();
      if (refreshed) {
        headers["Authorization"] = `Bearer ${refreshed}`;
        const retryResponse = await fetch(`${API_BASE}${url}`, {
          ...options,
          headers,
          ...(signal ? { signal } : {}),
        });

        if (await responseIsAuthExpiry(retryResponse)) {
          // Still an auth-expiry after a fresh token -> session is genuinely gone.
          notifySessionExpired();
          throw new ApiError(401, {
            detail: "Session expired. Please log in again.",
          });
        }
        return retryResponse;
      }
      // Refresh failed (no/expired cookie) — clear state and signal React.
      notifySessionExpired();
      throw new ApiError(401, {
        detail: "Session expired. Please log in again.",
      });
    }

    return response;
  };

  // 429 backoff loop (#1803): retry a bounded number of times before giving up.
  let response = await attemptOnce();
  for (let attempt = 0; response.status === 429 && attempt < MAX_THROTTLE_RETRIES; attempt++) {
    const waitMs = throttleBackoffMs(response.headers.get("Retry-After"), attempt);
    // Honour the caller's abort: sleep() rejects (AbortError) if it fires.
    await sleep(waitMs, signal);
    response = await attemptOnce();
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(response.status, errorData);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

/**
 * Decode a JWT's `exp` (seconds) without verifying it. Returns null if the token
 * is malformed. Used to decide on boot whether the cached access token is still
 * usable before deciding to silently refresh (issue #972).
 */
function getJwtExpiry(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    );
    return typeof json.exp === "number" ? json.exp : null;
  } catch {
    return null;
  }
}

/**
 * True when there is an access token in memory/sessionStorage that is not (about
 * to be) expired. A 10s skew avoids using a token that will die mid-request.
 */
export function hasUsableAccessToken(): boolean {
  const access = loadTokens()?.access;
  if (!access) return false;
  const exp = getJwtExpiry(access);
  if (exp === null) return true; // can't decode -> let the server decide
  return exp * 1000 > Date.now() + 10_000;
}

/**
 * Single-flight refresh state (issue #2359).
 *
 * After the app is idle the 15-min access token is expired, so the FIRST render
 * of the next screen fan-fires several requests that ALL 401 at once. Without
 * de-duplication each one independently POSTs `/auth/refresh/`; with refresh-
 * token ROTATION the server invalidates the just-used cookie, so the racing
 * refreshes can spuriously fail and one of them wrongly trips
 * `notifySessionExpired()`. `refreshInFlight` collapses every concurrent 401
 * onto ONE refresh round-trip, so all callers await the same result.
 *
 * `lastRefreshStartedAt` powers `isAuthRefreshActive()`: during (and for a short
 * grace after) a silent refresh, ANY transient error a half-rendered target
 * route throws is an expected refresh-window artefact, NOT a genuine crash, so
 * the error boundary recovers it silently instead of flashing "Something went
 * wrong" (issue #2359).
 */
let refreshInFlight: Promise<string | null> | null = null;
let lastRefreshStartedAt = 0;

/**
 * Milliseconds after a refresh STARTS during which a caught render error is
 * still treated as a refresh-window transient (the target route rendered before
 * its silently-refreshed data resolved). Kept short so a genuine crash that
 * merely happens to follow a refresh is not masked for long.
 */
const REFRESH_ACTIVITY_GRACE_MS = 3000;

/**
 * True while a silent access-token refresh is in flight, or within
 * `REFRESH_ACTIVITY_GRACE_MS` of one having started. The error boundary reads
 * this to distinguish a transient refresh-window error (recover silently) from a
 * genuine application crash (show the fallback) -- issue #2359.
 */
export function isAuthRefreshActive(): boolean {
  if (refreshInFlight) return true;
  return (
    lastRefreshStartedAt > 0 &&
    Date.now() - lastRefreshStartedAt < REFRESH_ACTIVITY_GRACE_MS
  );
}

/**
 * Silently mint a fresh access token from the HttpOnly refresh cookie (#972).
 *
 * Sends `credentials:"include"` so the browser attaches the cookie, and the
 * double-submit `X-CSRF-Refresh` header so the backend accepts the cookie path.
 * The rotated refresh token is set back into the cookie by the server (never
 * touches JS). On success persists + returns the new access token; on any
 * failure returns null. This is the canonical refresh path for boot, the 401
 * retry, and the multipart feedback POST.
 *
 * SINGLE-FLIGHT (#2359): concurrent callers share ONE in-flight refresh so the
 * fan-out of 401s after idle cannot race the rotating refresh cookie.
 */
export async function refreshSession(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  lastRefreshStartedAt = Date.now();
  const inflight = (async (): Promise<string | null> => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh/`, {
        method: "POST",
        // Attach the HttpOnly refresh cookie.
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [CSRF_REFRESH_HEADER]: readCsrfRefreshCookie(),
        },
        // Body is intentionally empty: the refresh token comes from the cookie.
        body: JSON.stringify({}),
      });

      if (!response.ok) return null;

      const data = await response.json();
      if (!data?.access) return null;
      // Refresh token is NEVER in the body any more -- only the access token.
      persistTokens({ access: data.access, refresh: "" });
      return data.access;
    } catch {
      return null;
    }
  })();
  refreshInFlight = inflight;
  try {
    return await inflight;
  } finally {
    refreshInFlight = null;
  }
}


/**
 * Extract a human-readable message from a DRF error body (#2227).
 *
 * DRF returns field-level validation errors as ``{ "<field>": ["msg", ...] }``
 * (or ``{ "<field>": "msg" }``) with NO top-level ``detail`` key, plus an
 * optional machine ``code``. The old ``ApiError`` only read ``data.detail``, so
 * every such 400 surfaced to the user as a bare ``"API Error 400"`` (the
 * paralegal-reported add-sale failure). This flattens the body to the first
 * usable message so the UI shows the real, specific reason (e.g. the
 * loom-report / signed-OTP hard-gate text) instead of a status code.
 *
 * Order: ``detail`` -> ``non_field_errors`` -> the first other field's message.
 * The machine ``code`` key is skipped (it is not user copy). Falls back to
 * ``API Error <status>`` when nothing usable is present (preserved so
 * ``friendlyError`` keeps mapping it to its generic copy).
 */
export function extractApiErrorMessage(
  status: number,
  data: Record<string, unknown>,
): string {
  const asMessage = (v: unknown): string | null => {
    if (typeof v === "string" && v.trim()) return v.trim();
    if (Array.isArray(v)) {
      const parts = v.filter((x): x is string => typeof x === "string" && !!x.trim());
      if (parts.length) return parts.join(" ");
    }
    return null;
  };
  if (!data || typeof data !== "object") return `API Error ${status}`;
  const detail = asMessage(data.detail);
  if (detail) return detail;
  const nonField = asMessage((data as Record<string, unknown>).non_field_errors);
  if (nonField) return nonField;
  for (const [key, value] of Object.entries(data)) {
    // ``code`` is a machine identifier, not user-facing copy.
    if (key === "code" || key === "detail" || key === "non_field_errors") continue;
    const msg = asMessage(value);
    if (msg) return msg;
  }
  return `API Error ${status}`;
}

/**
 * User-facing message for a failed multipart file upload (#3809).
 *
 * The record-document upload helpers used to read only ``detail``, but the
 * server's upload validation answers with a FIELD error
 * (``{"file": ["Unsupported file type. Allowed types: ..."]}``), so every
 * refusal surfaced as a bare "Upload failed (400)" with no reason. This routes
 * the body through {@link extractApiErrorMessage} so the user sees why, and
 * gives the edge proxy's body-size refusal (413, no JSON body) plain copy.
 */
export function uploadErrorMessage(status: number, data: unknown): string {
  if (status === 413) {
    return "This file is too large to upload. Try a smaller file, or scan at a lower resolution or as a PDF.";
  }
  const body = data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const msg = extractApiErrorMessage(status, body);
  return msg === `API Error ${status}` ? `Upload failed (${status})` : msg;
}

export class ApiError extends Error {
  status: number;
  data: Record<string, unknown>;

  constructor(status: number, data: Record<string, unknown>) {
    super(extractApiErrorMessage(status, data));
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

// --- Auth API functions ---

export async function login(username: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE}/auth/login/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(response.status, errorData);
  }

  const data: LoginResponse = await response.json();
  // The refresh token is now delivered ONLY via the HttpOnly cookie (#972); the
  // body carries just the access token. `data.refresh` may be undefined.
  persistTokens({ access: data.access, refresh: "" });
  // A fresh login replaces the old cookie, so an earlier failed sign-out is moot.
  setLogoutPending(false);
  return data;
}

/**
 * POST the server sign-out. True once the server has ended the session, or
 * there is no session cookie left to end.
 */
async function postLogout(): Promise<boolean> {
  // The endpoint is authenticated by the access bearer, and after an idle
  // spell the 15-min access token has usually expired: without a fresh one the
  // call 401s and the refresh cookie is never blacklisted or cleared. Mint one
  // from the cookie first. No CSRF twin cookie means no refresh cookie either.
  if (!hasUsableAccessToken()) {
    if (!readCsrfRefreshCookie()) return true;
    if (!(await refreshSession())) return false;
  }
  const access = loadTokens()?.access;
  // The refresh token is in the HttpOnly cookie, so logout must send
  // credentials + the CSRF double-submit header so the server can blacklist the
  // token AND clear the cookie (#972). The access bearer authenticates the call.
  try {
    const response = await fetch(`${API_BASE}/auth/logout/`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [CSRF_REFRESH_HEADER]: readCsrfRefreshCookie(),
        ...(access ? { Authorization: `Bearer ${access}` } : {}),
      },
      body: JSON.stringify({}),
    });
    // 400 = no / already-invalid refresh token: nothing left to revoke, and the
    // server cleared the cookies anyway.
    return response.ok || response.status === 400;
  } catch {
    return false;
  }
}

export async function logout(): Promise<void> {
  // Local state is cleared either way; a server sign-out that did not land is
  // remembered and retried on the next session restore.
  setLogoutPending(!(await postLogout()));
  clearTokens();
}

/**
 * Retry a server sign-out that did not land (D2-SEC-014). Resolves true when
 * nothing is pending any more; false means the session must not be restored.
 */
export async function completePendingLogout(): Promise<boolean> {
  if (!isLogoutPending()) return true;
  const done = await postLogout();
  setLogoutPending(!done);
  clearTokens();
  return done;
}

export async function getMe(): Promise<User> {
  return apiRequest<User>("/auth/me/");
}

/**
 * The effective role / preset / flags a real login of each workspace-switcher
 * persona carries (#1383). Admin-only metadata that powers the admin
 * "view-as-persona" PREVIEW: the frontend computes UI visibility against this
 * map, which is derived server-side from the canonical PRESET_FLAGS, so the
 * preview can never drift from a real persona login.
 *
 * SECURITY: this is a frontend visibility preview only -- it grants no
 * privilege and reduces nothing server-side.
 */
export interface PreviewPersona {
  role: "agent" | "admin";
  permission_preset: PermissionPreset;
  permission_flags: PermissionFlags;
}

export interface PermissionPresetsConfig {
  /** Keyed by switcher persona key (crm/paralegal/finance/manager/accounts/office_admin). */
  personas: Record<string, PreviewPersona>;
}

export async function getPermissionPresets(
  signal?: AbortSignal,
): Promise<PermissionPresetsConfig> {
  return apiRequest<PermissionPresetsConfig>(
    "/config/permission-presets/",
    { method: "GET" },
    signal,
  );
}

/**
 * Self-service profile update (D2-USR-006).
 *
 * PATCHes the signed-in user's OWN first/last name, contact email and phone.
 * The backend whitelists exactly these four fields -- role/permissions/staff
 * flags cannot be changed here (server-enforced). Returns the refreshed
 * profile so the auth context can update its cached user.
 */
export interface SelfProfilePayload {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  /** Required by the server only when ``email`` changes (SLA-SEC-004). */
  current_password?: string;
}

export async function updateMyProfile(data: SelfProfilePayload): Promise<User> {
  return apiRequest<User>("/auth/me/", {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function changePassword(
  oldPassword: string,
  newPassword: string
): Promise<void> {
  await apiRequest("/auth/change-password/", {
    method: "POST",
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  });
}

/**
 * Accept the CURRENT Terms & Conditions version (D8-ANA-W1-009). Clears
 * ``user.must_accept_terms`` server-side; the caller should ``refreshUser()``
 * afterwards so the client-side gate in ProtectedRoute.tsx picks it up.
 */
export async function acceptTerms(): Promise<{ detail: string; version: string }> {
  return apiRequest<{ detail: string; version: string }>("/auth/accept-terms/", {
    method: "POST",
  });
}

/**
 * Request a password-reset / account-setup link (#698). PUBLIC, no auth.
 * Always resolves (server is enumeration-safe and returns 200 regardless), so
 * the UI shows the same confirmation whether or not the identifier matched.
 */
export async function forgotPassword(identifier: {
  email?: string;
  username?: string;
}): Promise<{ detail: string }> {
  const response = await fetch(`${API_BASE}/auth/forgot-password/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(identifier),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(response.status, errorData);
  }
  return response.json();
}

/**
 * Consume a reset/setup token and set a new password (#698). PUBLIC, no auth.
 */
export async function resetPassword(
  token: string,
  newPassword: string
): Promise<{ detail: string }> {
  const response = await fetch(`${API_BASE}/auth/reset-password/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(response.status, errorData);
  }
  return response.json();
}

/**
 * True when `url` resolves to the app's own origin (a relative path, or an
 * absolute URL whose origin matches the page); false for a cross-origin URL.
 *
 * The document blob-fetch helpers use this to decide whether to attach the
 * `Authorization: Bearer` header. A SAME-ORIGIN gated app endpoint REQUIRES the
 * Bearer (the JWT lives in JS, not a cookie). A CROSS-ORIGIN presigned URL
 * (S3/MinIO `download_url`) is self-authenticated by its SigV4 query signature,
 * and AWS S3 rejects any request carrying BOTH a presigned signature AND an
 * Authorization header with HTTP 400 ("Only one auth mechanism allowed") --
 * MinIO silently ignored the stray header, AWS does not (issue #1490). So a
 * cross-origin presigned GET must be sent with NO Authorization header.
 *
 * A malformed URL falls back to same-origin (the safe gated-endpoint default).
 */
export function isSameOriginUrl(url: string): boolean {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return true;
  }
}

/**
 * True when `url` is a presigned object-storage (AWS S3 / MinIO) GET URL,
 * recognised by its SigV4 query signature (`X-Amz-Algorithm` / `X-Amz-Signature`
 * / `X-Amz-Credential`). Such a URL is self-authenticated by the signature, so it
 * must be fetched WITHOUT an Authorization header (AWS S3 rejects a request
 * carrying both with HTTP 400) and WITHOUT extra query params (they would break
 * the signature) -- issue #1490.
 *
 * Used together with `isSameOriginUrl`: the Bearer is withheld only from a
 * CROSS-ORIGIN presigned URL. The gated app `/api/...` download endpoint is
 * never presigned and keeps its Bearer whether it is same-origin (production
 * Caddy) or cross-origin (the dev `:8000` API server reached from the `:5174`
 * Vite origin, where `request.build_absolute_uri` yields an absolute :8000 URL).
 */
export function isPresignedObjectUrl(url: string): boolean {
  return /[?&]X-Amz-(Algorithm|Signature|Credential)=/i.test(url);
}

/** Loopback hostnames — the ONLY hosts the dev-origin exception below applies to. */
const LOOPBACK_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

/**
 * True when `url` resolves to an origin THIS DEPLOYMENT owns, and is therefore
 * an origin the caller's `Authorization: Bearer` may be sent to (#3309).
 *
 * `isSameOriginUrl` alone is not the discriminator the blob-fetch helpers can
 * use, because the dev gated endpoint is genuinely cross-origin (the Django
 * `:8000` API server vs. the Vite `:5174` SPA, since `build_absolute_uri` yields
 * an absolute `:8000` URL — #1490). But "not presigned" is not a safe stand-in
 * for "ours" either: some `download_url` values are attacker-influenced (a
 * `ListingPhoto.source_url` is a user-writable `URLField`, #2844), so a
 * shape-only check would hand the caller's JWT to any host that merely spells
 * its path like one of ours.
 *
 * The rule, therefore:
 *  - the page's own origin — always ours (production serves SPA + `/api/` from
 *    one origin behind nginx/Caddy);
 *  - a loopback origin, but ONLY when the page itself is on loopback — the
 *    local-dev `:5174` → `:8000` split. Derived from `window.location` at
 *    runtime rather than a build flag, so a production bundle served from a real
 *    domain can never take this branch whatever `VITE_*` was set at build time.
 *
 * Everything else (any public host, any non-http(s) scheme, a malformed URL) is
 * foreign: fetchable, but NEVER with a credential attached. Note this fails
 * CLOSED, unlike `isSameOriginUrl`, whose malformed-URL fallback is `true`.
 */
export function isAppOriginUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  let target: URL;
  let page: URL;
  try {
    target = new URL(url, window.location.href);
    page = new URL(window.location.href);
  } catch {
    return false;
  }
  if (target.protocol !== "http:" && target.protocol !== "https:") return false;
  if (target.origin === page.origin) return true;
  return (
    LOOPBACK_HOSTNAMES.has(page.hostname) &&
    LOOPBACK_HOSTNAMES.has(target.hostname)
  );
}

/**
 * The CRM's own ownership-scoped document/photo streaming routes, ANCHORED and
 * matched against a parsed `URL.pathname` -- never as a substring of the raw
 * string.
 *
 * Both halves matter. An unanchored substring match treats
 * `https://evil.tld/x?u=/api/v1/documents/1/download/` as one of ours, and a
 * path-shape match with no origin check treats
 * `https://evil.tld/api/v1/documents/1/download/x.png` as one of ours -- and a
 * `ListingPhoto.source_url` is a user-writable `URLField` (#2844) that lands
 * straight in `download_url`, so both shapes are attacker-supplied in practice.
 * Either one would have sent the viewer's JWT to the attacker's host: an agent
 * could plant such a photo on their own listing and harvest the access token of
 * every colleague (and admin) who opened its Photos tab.
 */
const GATED_PATH_RE =
  /^\/api\/v1\/(?:listing-photos|listing-documents|documents)\/\d+\/download\/$/;

/**
 * Resolve `src` to the gated URL we may fetch WITH the caller's Bearer, or
 * `null` when it is anything else (presigned, CDN, foreign host, junk).
 *
 * The origin pin mirrors the backend's `_pin_next_to_base` (#3309): the URL is
 * parsed, its origin required to be one of ours, and the request re-issued from
 * `origin + pathname + search` alone -- so userinfo (`https://u:p@host/`) and
 * fragment are dropped rather than forwarded.
 */
export function resolveGatedDownloadUrl(
  url: string | null | undefined,
): string | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url, window.location.href);
  } catch {
    return null;
  }
  // Origin FIRST: `isAppOriginUrl` also rejects any non-http(s) scheme, so an
  // opaque `javascript:/api/v1/...` path can never reach the shape check below.
  if (!isAppOriginUrl(parsed.href)) return null;
  if (!GATED_PATH_RE.test(parsed.pathname)) return null;
  return `${parsed.origin}${parsed.pathname}${parsed.search}`;
}

export { apiRequest };
export type { User, LoginResponse, Tokens };
